import { useState } from "react";
import { Pencil, History, Archive, RotateCcw, Plus, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AgreementEditorModal from "@/components/agreements/AgreementEditorModal";
import VersionsModal from "@/components/agreements/VersionsModal";

// Agreement list with create / edit / versions / archive. `actionBase` e.g. /admin/agreements-hub/agreements
export default function AgreementsList({ items = [], base, archiveUrl, options, targets, canEdit = true, template = false,
  onChanged, onUseTemplate, emptyText = "No agreements yet.", testid = "agreements-list", newLabel = "New agreement" }) {
  const [editing, setEditing] = useState(null);
  const [versions, setVersions] = useState(null);
  const archive = async (a) => {
    try { await api.post(archiveUrl(a, a.archived ? "restore" : "archive")); toast.success(a.archived ? "Restored" : "Archived"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div data-testid={testid}>
      {canEdit && (
        <button onClick={() => setEditing({})} data-testid={`${testid}-new`} className="eyw-btn !py-2 text-sm mb-4"><Plus size={15} /> {newLabel}</button>
      )}
      {items.length === 0 ? <p className="text-sm text-[#4A5D6B]">{emptyText}</p> : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id} className={`rounded-2xl border border-[#E5E0D8] bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${a.archived ? "opacity-60" : ""}`} data-testid={`agreement-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#1A242B] truncate">{a.title}</p>
                <p className="text-xs text-[#8FA89B] mt-0.5">
                  {a.type} · v{a.version}{a.signed_count != null && ` · ${a.signed_count} signed`}{a.pdf_path && " · PDF"}
                  {a.archived ? " · archived" : !a.active ? " · inactive" : ""}
                  {(a.service_ids?.length || a.package_ids?.length || a.membership_keys?.length || a.location_ids?.length || a.service_types?.length) ? ` · assigned to ${(a.service_ids?.length || 0) + (a.package_ids?.length || 0) + (a.membership_keys?.length || 0) + (a.location_ids?.length || 0) + (a.service_types?.length || 0)}` : ""}
                  {a.required_for?.includes("membership") && " · all members"}
                </p>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {onUseTemplate && <button onClick={() => onUseTemplate(a)} data-testid={`agreement-use-template-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Copy size={13} /> Use</button>}
                <button onClick={() => setVersions(a)} data-testid={`agreement-versions-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><History size={13} /> Versions</button>
                {canEdit && <button onClick={() => setEditing(a)} data-testid={`agreement-edit-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>}
                {canEdit && (
                  <button onClick={() => archive(a)} data-testid={`agreement-archive-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs">
                    {a.archived ? <><RotateCcw size={13} /> Restore</> : <><Archive size={13} /> Archive</>}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <AgreementEditorModal agreement={editing.id ? editing : null} base={base} options={options} targets={targets} template={template}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
      )}
      {versions && <VersionsModal agreement={versions} onClose={() => setVersions(null)} />}
    </div>
  );
}
